// /weighbridge/settings/barriers/:enc_id — Phase C2 Group 2.
//
// Create + edit form for a single barrier / signal. Two cards:
//
//   1. General — name, code, type, protocol, yard/bridge, default
//      state, connection-config (protocol-conditional fields packed
//      into `connection_config_encrypted` as JSON on submit), is_active.
//   2. Trigger Rules — local-state array of declarative event → state
//      rules; a Modal hosts the rule form. Full array is sent on Save.
//
// yard_id is REQUIRED and immutable. connection_config_encrypted is
// never returned in any response (server encrypts at rest).

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Plus, Trash2, X } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Modal } from '../../../components/ui/Modal/Modal';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import {
  useCreateBarrier,
  useDeleteBarrier,
  useUpdateBarrier,
  useWbBarrier,
  type CreateBarrierBody,
  type UpdateBarrierBody,
  type WbBarrierProtocol,
  type WbBarrierType,
  type WbTriggerEvent,
  type WbTriggerRule,
} from '../hooks/useWbBarriers';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './WbBarrierDetailPage.module.css';

interface YardSummary {
  enc_id: string;
  name: string;
}
interface WeighbridgeSummary {
  enc_id: string;
  name: string;
  yard: { enc_id: string; name: string };
}

const TYPE_OPTIONS: ReadonlyArray<{ value: WbBarrierType; label: string }> = [
  { value: 'traffic_light', label: 'Traffic Light' },
  { value: 'barrier_arm', label: 'Barrier Arm' },
  { value: 'siren', label: 'Siren' },
  { value: 'generic_signal', label: 'Generic Signal' },
];

const PROTOCOL_OPTIONS: ReadonlyArray<{ value: WbBarrierProtocol; label: string }> = [
  { value: 'usb_relay_hid', label: 'USB Relay (HID)' },
  { value: 'modbus_tcp', label: 'Modbus TCP' },
  { value: 'mqtt', label: 'MQTT' },
  { value: 'gpio', label: 'GPIO' },
];

const EVENT_OPTIONS: ReadonlyArray<{ value: WbTriggerEvent; label: string }> = [
  { value: 'ticket.completed', label: 'Ticket: completed' },
  { value: 'ticket.gross_captured', label: 'Ticket: gross captured' },
  { value: 'weight.stable_no_action', label: 'Weight: stable, no action' },
  { value: 'overweight.detected', label: 'Overweight detected' },
  { value: 'manual', label: 'Manual' },
];

const MAX_RULES = 20;

// -- Connection config sub-state -----------------------------------------

interface ConnState {
  device_path: string;
  tcp_host: string;
  tcp_port: number;
  unit_id: number;
  broker_url: string;
  topic_open: string;
  topic_close: string;
  pin_number: number;
}

const EMPTY_CONN: ConnState = {
  device_path: '',
  tcp_host: '',
  tcp_port: 502,
  unit_id: 1,
  broker_url: '',
  topic_open: '',
  topic_close: '',
  pin_number: 0,
};

function buildConnectionConfig(protocol: WbBarrierProtocol, conn: ConnState): string | null {
  let obj: Record<string, unknown>;
  switch (protocol) {
    case 'usb_relay_hid':
      if (!conn.device_path.trim()) return null;
      obj = { device_path: conn.device_path.trim() };
      break;
    case 'modbus_tcp':
      if (!conn.tcp_host.trim()) return null;
      obj = {
        tcp_host: conn.tcp_host.trim(),
        tcp_port: conn.tcp_port,
        unit_id: conn.unit_id,
      };
      break;
    case 'mqtt':
      if (!conn.broker_url.trim()) return null;
      obj = {
        broker_url: conn.broker_url.trim(),
        topic_open: conn.topic_open.trim(),
        topic_close: conn.topic_close.trim(),
      };
      break;
    case 'gpio':
      obj = { pin_number: conn.pin_number };
      break;
    default:
      return null;
  }
  return JSON.stringify(obj);
}

// -- Main form state -----------------------------------------------------

interface FormState {
  name: string;
  code: string;
  type: WbBarrierType;
  protocol: WbBarrierProtocol;
  yard_id: string;
  weighbridge_id: string;
  default_state: string;
  is_active: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  code: '',
  type: 'traffic_light',
  protocol: 'usb_relay_hid',
  yard_id: '',
  weighbridge_id: '',
  default_state: 'closed',
  is_active: true,
};

// -- Rule modal state ----------------------------------------------------

interface RuleDraft {
  event: WbTriggerEvent;
  target_state: string;
  hold_ms: string;
  condition: string;
}

const EMPTY_RULE: RuleDraft = {
  event: 'ticket.completed',
  target_state: '',
  hold_ms: '',
  condition: '',
};

export function WbBarrierDetailPage(): JSX.Element {
  const navigate = useNavigate();
  const { enc_id } = useParams<{ enc_id: string }>();
  const isNew = enc_id === 'new' || !enc_id;
  const encIdForEdit = isNew ? '' : enc_id;

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [conn, setConn] = useState<ConnState>(EMPTY_CONN);
  const [rules, setRules] = useState<WbTriggerRule[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Rule modal
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [ruleDraft, setRuleDraft] = useState<RuleDraft>(EMPTY_RULE);
  const [ruleError, setRuleError] = useState<string | null>(null);

  const barrierQuery = useWbBarrier(encIdForEdit || undefined);
  const createBarrier = useCreateBarrier();
  const updateBarrier = useUpdateBarrier(encIdForEdit);
  const deleteBarrier = useDeleteBarrier(encIdForEdit);

  // Hydrate the form once the barrier detail lands.
  useEffect(() => {
    const b = barrierQuery.data?.barrier;
    if (!b) return;
    setForm({
      name: b.name,
      code: b.code,
      type: b.type,
      protocol: b.protocol,
      yard_id: b.yard.enc_id,
      weighbridge_id: b.weighbridge?.enc_id ?? '',
      default_state: b.default_state,
      is_active: b.is_active,
    });
    setRules(b.trigger_config_json ? [...b.trigger_config_json] : []);
  }, [barrierQuery.data?.barrier]);

  // Yard + weighbridge dropdowns -----------------------------------------
  const yardsQuery = useQuery({
    queryKey: queryKeys.wb.yards(),
    queryFn: async () => {
      const res = await apiClient.get<{ yards: YardSummary[] }>(endpoints.wb.yards);
      return res.data;
    },
  });
  const bridgesQuery = useQuery({
    queryKey: queryKeys.wb.weighbridges(),
    queryFn: async () => {
      const res = await apiClient.get<{ weighbridges: WeighbridgeSummary[] }>(
        endpoints.wb.weighbridges,
      );
      return res.data;
    },
  });
  const yards = yardsQuery.data?.yards ?? [];
  const bridges = bridgesQuery.data?.weighbridges ?? [];

  function handleBridgeChange(nextBridgeEnc: string) {
    setForm((prev) => {
      if (nextBridgeEnc === '') return { ...prev, weighbridge_id: '' };
      const bridge = bridges.find((b) => b.enc_id === nextBridgeEnc);
      return {
        ...prev,
        weighbridge_id: nextBridgeEnc,
        yard_id: bridge?.yard.enc_id ?? prev.yard_id,
      };
    });
  }

  const subtitle = useMemo(
    () => (isNew ? 'Add a new barrier or signal.' : 'Edit barrier configuration.'),
    [isNew],
  );

  // -- Rule modal --------------------------------------------------------

  function openRuleModal() {
    setRuleDraft(EMPTY_RULE);
    setRuleError(null);
    setRuleModalOpen(true);
  }

  function handleSaveRule() {
    setRuleError(null);
    if (!ruleDraft.target_state.trim()) {
      setRuleError('Target state is required.');
      return;
    }
    if (ruleDraft.target_state.length > 50) {
      setRuleError('Target state must be 50 characters or fewer.');
      return;
    }
    let parsedCondition: Record<string, unknown> | undefined;
    if (ruleDraft.condition.trim()) {
      try {
        const parsed = JSON.parse(ruleDraft.condition);
        if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
          throw new Error('Condition must be a JSON object.');
        }
        parsedCondition = parsed as Record<string, unknown>;
      } catch (err) {
        setRuleError(
          err instanceof Error ? `Invalid condition JSON: ${err.message}` : 'Invalid condition JSON.',
        );
        return;
      }
    }
    let holdMs: number | undefined;
    if (ruleDraft.hold_ms.trim()) {
      const parsed = Number.parseInt(ruleDraft.hold_ms, 10);
      if (Number.isNaN(parsed) || parsed < 0) {
        setRuleError('Hold (ms) must be a non-negative integer.');
        return;
      }
      holdMs = parsed;
    }
    if (rules.length >= MAX_RULES) {
      setRuleError(`At most ${MAX_RULES} rules per barrier.`);
      return;
    }
    const next: WbTriggerRule = {
      event: ruleDraft.event,
      target_state: ruleDraft.target_state.trim(),
    };
    if (parsedCondition !== undefined) next.condition = parsedCondition;
    if (holdMs !== undefined) next.hold_ms = holdMs;
    setRules((prev) => [...prev, next]);
    setRuleModalOpen(false);
  }

  function handleDeleteRule(idx: number) {
    setRules((prev) => prev.filter((_, i) => i !== idx));
  }

  // -- Submit ------------------------------------------------------------

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    if (!form.code.trim()) {
      setError('Code is required.');
      return;
    }
    if (form.code.length > 20) {
      setError('Code must be 20 characters or fewer.');
      return;
    }
    if (!form.yard_id) {
      setError('Yard is required.');
      return;
    }
    if (!form.default_state.trim()) {
      setError('Default state is required.');
      return;
    }

    const connectionConfig = buildConnectionConfig(form.protocol, conn);

    if (isNew) {
      const body: CreateBarrierBody = {
        yard_id: form.yard_id,
        name: form.name.trim(),
        code: form.code.trim().toUpperCase(),
        type: form.type,
        protocol: form.protocol,
        default_state: form.default_state.trim(),
        is_active: form.is_active,
        trigger_config_json: rules,
      };
      if (form.weighbridge_id) body.weighbridge_id = form.weighbridge_id;
      if (connectionConfig) body.connection_config_encrypted = connectionConfig;
      createBarrier.mutate(body, {
        onSuccess: () => navigate('/weighbridge/settings/barriers'),
        onError: (err) => setError(extractErrorMessage(err)),
      });
      return;
    }

    const body: UpdateBarrierBody = {
      name: form.name.trim(),
      code: form.code.trim().toUpperCase(),
      type: form.type,
      protocol: form.protocol,
      weighbridge_id: form.weighbridge_id ? form.weighbridge_id : null,
      default_state: form.default_state.trim(),
      is_active: form.is_active,
      trigger_config_json: rules,
    };
    if (connectionConfig) body.connection_config_encrypted = connectionConfig;
    updateBarrier.mutate(body, {
      onSuccess: () => navigate('/weighbridge/settings/barriers'),
      onError: (err) => setError(extractErrorMessage(err)),
    });
  }

  function handleDelete() {
    deleteBarrier.mutate(undefined, {
      onSuccess: () => navigate('/weighbridge/settings/barriers'),
      onError: (err) => {
        setError(extractErrorMessage(err));
        setConfirmOpen(false);
      },
    });
  }

  const saving = createBarrier.isPending || updateBarrier.isPending;
  const loadingExisting = !isNew && barrierQuery.isLoading;

  return (
    <div className={styles.page}>
      <PageHeader
        title={isNew ? 'New Barrier' : 'Edit Barrier'}
        subtitle={subtitle}
        actions={
          <Button
            variant="secondary"
            icon={ArrowLeft}
            onClick={() => navigate('/weighbridge/settings/barriers')}
          >
            Back
          </Button>
        }
      />

      {loadingExisting ? (
        <Card>
          <p className={styles.note}>Loading barrier…</p>
        </Card>
      ) : (
        <form onSubmit={handleSubmit} className={styles.form}>
          <Card>
            <h2 className={styles.sectionHeading}>General</h2>
            <div className={styles.grid}>
              <label className={styles.field}>
                <span>Name</span>
                <input
                  required
                  maxLength={200}
                  value={form.name}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                />
              </label>
              <label className={styles.field}>
                <span>Code</span>
                <input
                  required
                  maxLength={20}
                  value={form.code}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, code: e.target.value }))
                  }
                  onBlur={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      code: e.target.value.toUpperCase(),
                    }))
                  }
                />
              </label>
              <label className={styles.field}>
                <span>Type</span>
                <select
                  value={form.type}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      type: e.target.value as WbBarrierType,
                    }))
                  }
                >
                  {TYPE_OPTIONS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.field}>
                <span>Protocol</span>
                <select
                  value={form.protocol}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      protocol: e.target.value as WbBarrierProtocol,
                    }))
                  }
                >
                  {PROTOCOL_OPTIONS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.field}>
                <span>Yard</span>
                <select
                  required
                  value={form.yard_id}
                  disabled={!isNew}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, yard_id: e.target.value }))
                  }
                >
                  <option value="">— Select yard —</option>
                  {yards.map((y) => (
                    <option key={y.enc_id} value={y.enc_id}>
                      {y.name}
                    </option>
                  ))}
                </select>
                {!isNew ? (
                  <p className={styles.helpText}>
                    Yard cannot be changed after the barrier is created.
                  </p>
                ) : null}
              </label>
              <label className={styles.field}>
                <span>Assigned bridge (optional)</span>
                <select
                  value={form.weighbridge_id}
                  onChange={(e) => handleBridgeChange(e.target.value)}
                >
                  <option value="">— None —</option>
                  {bridges.map((b) => (
                    <option key={b.enc_id} value={b.enc_id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.field}>
                <span>Default state</span>
                <input
                  required
                  maxLength={50}
                  value={form.default_state}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, default_state: e.target.value }))
                  }
                  placeholder="closed | red | off"
                />
                <p className={styles.helpText}>
                  Safety fallback when no rule matches — e.g. &lsquo;closed&rsquo;, &lsquo;red&rsquo;, &lsquo;off&rsquo;.
                </p>
              </label>
              <div className={styles.field}>
                <label className={styles.toggleRow}>
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, is_active: e.target.checked }))
                    }
                  />
                  <span>Active</span>
                </label>
              </div>
            </div>

            {/* Protocol-conditional connection config -------------------- */}
            <div style={{ marginTop: '1rem' }}>
              <h3
                style={{
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  margin: '0 0 0.5rem 0',
                  color: 'var(--color-text-secondary)',
                }}
              >
                Connection
              </h3>
              <div className={styles.grid}>
                {form.protocol === 'usb_relay_hid' ? (
                  <label className={`${styles.field} ${styles.fullRow}`}>
                    <span>Device path</span>
                    <input
                      placeholder="/dev/hidraw0"
                      value={conn.device_path}
                      onChange={(e) =>
                        setConn((prev) => ({ ...prev, device_path: e.target.value }))
                      }
                    />
                  </label>
                ) : null}

                {form.protocol === 'modbus_tcp' ? (
                  <>
                    <label className={styles.field}>
                      <span>TCP host</span>
                      <input
                        placeholder="192.168.1.50"
                        value={conn.tcp_host}
                        onChange={(e) =>
                          setConn((prev) => ({ ...prev, tcp_host: e.target.value }))
                        }
                      />
                    </label>
                    <label className={styles.field}>
                      <span>TCP port</span>
                      <input
                        type="number"
                        min={1}
                        max={65535}
                        value={conn.tcp_port}
                        onChange={(e) =>
                          setConn((prev) => ({
                            ...prev,
                            tcp_port: Number.parseInt(e.target.value, 10) || 0,
                          }))
                        }
                      />
                    </label>
                    <label className={styles.field}>
                      <span>Unit ID</span>
                      <input
                        type="number"
                        min={0}
                        max={247}
                        value={conn.unit_id}
                        onChange={(e) =>
                          setConn((prev) => ({
                            ...prev,
                            unit_id: Number.parseInt(e.target.value, 10) || 0,
                          }))
                        }
                      />
                    </label>
                  </>
                ) : null}

                {form.protocol === 'mqtt' ? (
                  <>
                    <label className={`${styles.field} ${styles.fullRow}`}>
                      <span>Broker URL</span>
                      <input
                        placeholder="mqtt://broker.example.com:1883"
                        value={conn.broker_url}
                        onChange={(e) =>
                          setConn((prev) => ({ ...prev, broker_url: e.target.value }))
                        }
                      />
                    </label>
                    <label className={styles.field}>
                      <span>Topic (open)</span>
                      <input
                        placeholder="yard/bridge/barrier/open"
                        value={conn.topic_open}
                        onChange={(e) =>
                          setConn((prev) => ({ ...prev, topic_open: e.target.value }))
                        }
                      />
                    </label>
                    <label className={styles.field}>
                      <span>Topic (close)</span>
                      <input
                        placeholder="yard/bridge/barrier/close"
                        value={conn.topic_close}
                        onChange={(e) =>
                          setConn((prev) => ({ ...prev, topic_close: e.target.value }))
                        }
                      />
                    </label>
                  </>
                ) : null}

                {form.protocol === 'gpio' ? (
                  <label className={styles.field}>
                    <span>Pin number</span>
                    <input
                      type="number"
                      min={0}
                      max={127}
                      value={conn.pin_number}
                      onChange={(e) =>
                        setConn((prev) => ({
                          ...prev,
                          pin_number: Number.parseInt(e.target.value, 10) || 0,
                        }))
                      }
                    />
                  </label>
                ) : null}
              </div>
              <p className={styles.helpText}>
                Connection config stored encrypted; cannot be read back. Re-enter to overwrite.
              </p>
            </div>
          </Card>

          <Card>
            <h2 className={styles.sectionHeading}>Trigger Rules</h2>
            {rules.length === 0 ? (
              <p className={styles.rulesEmpty}>
                No rules yet. The barrier will stay in its default state until a rule is added.
              </p>
            ) : (
              <table className={styles.rulesTable}>
                <thead>
                  <tr>
                    <th>Event</th>
                    <th>Target state</th>
                    <th>Hold (ms)</th>
                    <th>Condition</th>
                    <th aria-label="Remove" />
                  </tr>
                </thead>
                <tbody>
                  {rules.map((r, idx) => {
                    const conditionStr = r.condition
                      ? JSON.stringify(r.condition)
                      : '';
                    const preview =
                      conditionStr.length > 40
                        ? `${conditionStr.slice(0, 40)}…`
                        : conditionStr;
                    return (
                      <tr key={`${r.event}-${idx}`}>
                        <td>{r.event}</td>
                        <td>{r.target_state}</td>
                        <td>{r.hold_ms ?? '—'}</td>
                        <td className={styles.conditionPreview}>{preview || '—'}</td>
                        <td>
                          <Button
                            type="button"
                            variant="icon"
                            size="sm"
                            aria-label="Remove rule"
                            icon={X}
                            onClick={() => handleDeleteRule(idx)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            <div className={styles.addRule}>
              <Button
                type="button"
                variant="secondary"
                icon={Plus}
                onClick={openRuleModal}
                disabled={rules.length >= MAX_RULES}
              >
                Add Rule
              </Button>
              {rules.length >= MAX_RULES ? (
                <p className={styles.helpText}>
                  Maximum {MAX_RULES} rules per barrier.
                </p>
              ) : null}
            </div>
          </Card>

          {error ? <p className={styles.error}>{error}</p> : null}

          <div className={styles.formActions}>
            {!isNew ? (
              <Button
                type="button"
                variant="danger"
                icon={Trash2}
                onClick={() => setConfirmOpen(true)}
                disabled={saving || deleteBarrier.isPending}
              >
                Delete
              </Button>
            ) : null}
            <div className={styles.formActionsSpacer} />
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/weighbridge/settings/barriers')}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      )}

      <Modal
        open={ruleModalOpen}
        onClose={() => setRuleModalOpen(false)}
        title="Add Trigger Rule"
        size="md"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setRuleModalOpen(false)}
            >
              Cancel
            </Button>
            <Button type="button" variant="primary" onClick={handleSaveRule}>
              Save Rule
            </Button>
          </>
        }
      >
        <div className={styles.modalForm}>
          <label className={styles.modalField}>
            <span>Event</span>
            <select
              value={ruleDraft.event}
              onChange={(e) =>
                setRuleDraft((prev) => ({
                  ...prev,
                  event: e.target.value as WbTriggerEvent,
                }))
              }
            >
              {EVENT_OPTIONS.map((ev) => (
                <option key={ev.value} value={ev.value}>
                  {ev.label}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.modalField}>
            <span>Target state</span>
            <input
              maxLength={50}
              value={ruleDraft.target_state}
              onChange={(e) =>
                setRuleDraft((prev) => ({ ...prev, target_state: e.target.value }))
              }
              placeholder="green | open | on"
            />
          </label>
          <label className={styles.modalField}>
            <span>Hold (ms) — optional</span>
            <input
              type="number"
              min={0}
              value={ruleDraft.hold_ms}
              onChange={(e) =>
                setRuleDraft((prev) => ({ ...prev, hold_ms: e.target.value }))
              }
              placeholder="e.g. 5000"
            />
          </label>
          <label className={styles.modalField}>
            <span>Condition JSON (optional)</span>
            <textarea
              rows={3}
              value={ruleDraft.condition}
              onChange={(e) =>
                setRuleDraft((prev) => ({ ...prev, condition: e.target.value }))
              }
              placeholder='{"overweight_kg": 1000}'
            />
          </label>
          {ruleError ? <p className={styles.modalError}>{ruleError}</p> : null}
        </div>
      </Modal>

      <ConfirmModal
        open={confirmOpen}
        title="Delete barrier?"
        message="This barrier will be soft-deleted. You can recreate it later if needed."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
        loading={deleteBarrier.isPending}
      />
    </div>
  );
}
